import {Injectable, OnDestroy} from "@angular/core";
import {DocumentValidationState, IDocumentElement, IDocumentFile} from "../interfaces/Iself.scheduling";
import {BehaviorSubject, Observable} from "rxjs";
import {DOC_ORIENTATION, NgxImageCompressService} from "ngx-image-compress";
import {SelfSchedulingProxy} from "./self.scheduling.proxy";
import {SystemConfigurationValues} from "../../../common/configuration/services/system.configuration.manager";
import * as _ from "lodash";

@Injectable()
export class SelfSchedulingDocumentAcquisitionService implements OnDestroy {

  private readonly  allowedImageFormats = ["image/gif", "image/jpeg", "image/jpg", "image/png", "image/bmp"];
  readonly  acceptedFileFormatString = "image/gif, image/jpeg, image/jpg, image/png, image/bmp, application/pdf";
  private uploadCount: number = 0;
  private acquiredDocuments: { [index: number]: IDocumentFile; } = {}

  private acquiredDocumentPreviousState: { [index: number]: IDocumentFile; } = {}

  private storedDocumentList: BehaviorSubject<IDocumentElement[]> = new BehaviorSubject(null);
  get documentList(): Observable<IDocumentElement[]> {
    return this.storedDocumentList.asObservable();
  }

  constructor(private imageCompress: NgxImageCompressService, private selfSchedulingProxy: SelfSchedulingProxy,
              private cfg: SystemConfigurationValues) {

  }

  public handleAcquiredDocument(documents: FileList) {
    const documentArr = Array.from(documents);

    documentArr.forEach((document)=> {

      this.getValidationState(document).then((validity) => {
        const reader = new FileReader();

        reader.onload = (event: any) => {
          const origDataUrl =  event.target.result;
          const mimeType = this.getDocumentType(origDataUrl);

          if(mimeType === "application/pdf") {
            // PDF files
            const size = origDataUrl.split(",")[1].length;
            this.storeDocument(origDataUrl, mimeType, size, validity)
          } else if (this.allowedImageFormats.indexOf(mimeType) !== -1) {
            // IMAGE files
            const compressionQuality = this.cfg.PP_ScanDocumentImageQualityPercentage;
            const targetHeightInPixels = this.cfg.PP_ScanDocumentMaxPixelSizeLongEdge;

            this.getCompressionRatio(origDataUrl, targetHeightInPixels)
              .then((compressionValue) => {
                this.imageCompress.compressFile(origDataUrl, DOC_ORIENTATION.Up, compressionValue, compressionQuality)
                  .then(compressedFile => {
                    this.getByteSizeFromDataUrlAsBlob(compressedFile).then((blobSize) => {
                      this.storeDocument(compressedFile, mimeType, blobSize, validity)
                    })
                  })
              })
            }
        };

        reader.onerror = (event: any) => {
          // todo: error handling for file upload?
        };

        if (validity === DocumentValidationState.ValidFile) {
          reader.readAsDataURL(document);
        } else {
          // if file is not valid don't bother reading it, store an empty content with the invalid state
          this.storeDocument("", document.type, 0, validity);
        }
      })
    });
  }

  public deleteDocument(index: number) {
    delete this.acquiredDocuments[index];
    this.updateStoredDocumentList();
  }

  public getDocumentUrl(index: number): string {
    return this.acquiredDocuments[index].content;
  }

  public getData(): Promise<IDocumentFile[]> {
    let promise = new Promise<IDocumentFile[]> ((resolve) => {

      Promise.all(
        Object.keys(this.acquiredDocuments).map(currIndex => {

          const ext = this.acquiredDocuments[+currIndex].docExtension;

          if(ext === "pdf") {
            return {
              content: this.acquiredDocuments[+currIndex].content,
              name: this.acquiredDocuments[+currIndex].name,
              size: this.acquiredDocuments[+currIndex].size,
              docExtension: ext,
              validationState: this.acquiredDocuments[+currIndex].validationState
            }
          } else {
            // get mime type again for compressed images
            return this.getMimeType(this.acquiredDocuments[+currIndex].content).then((compressedMimeType) => {
              return {
                content: this.acquiredDocuments[+currIndex].content,
                name: this.acquiredDocuments[+currIndex].name,
                size: this.acquiredDocuments[+currIndex].size,
                docExtension: compressedMimeType.split("/")[1],
                validationState: this.acquiredDocuments[+currIndex].validationState
              }
            })
          }
        })
      ).then((resultingArray) => {
        resolve(resultingArray);
      })
    });
    return promise;
  }

  public clearData()
  {
    this.uploadCount = 0;
    this.acquiredDocuments = {};
    this.storedDocumentList = new BehaviorSubject(null);
    this.acquiredDocumentPreviousState = {};
  }

  private getDocumentType(dataUrl: string) : string {
    const base64ContentArray = dataUrl.split(",")
    const mimeType = base64ContentArray[0].match(/[^:\s*]\w+\/[\w-+\d.]+(?=[;| ])/)[0]
    return mimeType;
  }

  private storeDocument(dataUrl: string, mimeType: string, size: number, validityState: DocumentValidationState) {
    const nextIndex = this.uploadCount++;

    this.acquiredDocuments[nextIndex] = {content: dataUrl, docExtension: mimeType.split("/")[1], name: 'PortalDoc_' + nextIndex, size: size / 1000, validationState: validityState};
    this.updateStoredDocumentList()
  }

  private updateStoredDocumentList() {
    // Refresh the list of stored documents for UI updates
    this.storedDocumentList.next(Object.keys(this.acquiredDocuments).map(currIndex => {
      return {
        index: +currIndex,
        name: this.acquiredDocuments[+currIndex].name,
        size: this.acquiredDocuments[+currIndex].size,
        isPdf: this.acquiredDocuments[+currIndex].docExtension === "pdf",
        validationState: this.acquiredDocuments[+currIndex].validationState
      }
    }));
  }

  public restorePreviousState() {
    if(this.acquiredDocumentPreviousState && Object.keys(this.acquiredDocumentPreviousState).length === 0) {
      this.clearData();
    } else {
      this.acquiredDocuments = this.acquiredDocumentPreviousState;
      this.updateStoredDocumentList();
    }
  }

  public savePreviousState() {
    if(this.acquiredDocuments) {
      this.acquiredDocumentPreviousState = _.cloneDeep(this.acquiredDocuments);
    }
  }

  private getValidationState(file: File): Promise<DocumentValidationState> {
    let promise = new Promise<DocumentValidationState>((resolve) => {
      const weakMimeType = file.type;

      this.getMimeType(file).then((mimeType) => {
        // if the file extension matches the real mime type from the file header signature
        if (mimeType === weakMimeType) {

          resolve(DocumentValidationState.ValidFile);
        } else {
          resolve(DocumentValidationState.IncorrectFileType);
        }
      }).catch((error) => {
        resolve(DocumentValidationState.IncorrectFileType)
      })
    });

    return promise;
  }

  private getCompressionRatio(dataUrl: string, targetHeightPx: number): Promise<number> {
    let compressionRatioPromise = new Promise<number>((resolve) => {

      let img = new Image();

      img.onload = function(){
        const valueForHeight = Math.max(img.width, img.height);
        targetHeightPx >= valueForHeight ?
          resolve(100) :
          resolve((targetHeightPx / valueForHeight) * 100);
      };

      img.src = dataUrl;
    })

    return compressionRatioPromise;
  }

  private getByteSizeFromDataUrlAsBlob(dataUrl: string): Promise<number> {
    let fileSizePromise = new Promise<number>((resolve) => {
      fetch(dataUrl)
        .then(res => res.blob())
        .then(blob => resolve(blob.size))
    });

    return fileSizePromise;
  }

  public getMimeType(file: Blob | string): Promise<string>{
    let promise = new Promise<string>((resolve) => {
      const fileReader = new FileReader()
      let fileBlobHeaderSlice: Blob = null;

      fileReader.onloadend = (evt: any) => {
        if (evt.target.readyState === FileReader.DONE) {
          const uint = new Uint8Array(evt.target.result);
          let bytes = [];

          uint.forEach((byte) => {
            bytes.push(byte.toString(16))
          });

          const hex = bytes.join('').toUpperCase();
          const mimeType = this.getMimeTypeFromSignature(hex);

          resolve(mimeType);
        }
      }

      if(file instanceof Blob) {
        fileBlobHeaderSlice = file.slice(0, 4);
        fileReader.readAsArrayBuffer(fileBlobHeaderSlice)
      } else {
        fetch(file)
          .then(res => res.blob())
          .then(blob => {
            fileBlobHeaderSlice = blob.slice(0,4);
            fileReader.readAsArrayBuffer(fileBlobHeaderSlice)
          })
      }
    });

    return promise;
  }

  private getMimeTypeFromSignature(signature: string): string {
    switch (signature) {
      case '89504E47':
        return 'image/png';
        break;
      case '47494638':
        return 'image/gif';
        break;
      case '25504446':
        return 'application/pdf';
        break;
      case 'FFD8FFE1':
      case 'FFD8FFDB':
      case 'FFD8FFE0':
      case 'FFD8FFEE':
        return 'image/jpeg';
        break;
      default:
        if (signature.startsWith('424D')) {
          return 'image/bmp';
          break;
        }
        return 'Unknown filetype';
        break;
    }
  }

  ngOnDestroy(): void {
  }
}

